import { motion } from "framer-motion"
import { FiBarChart2, FiCheckCircle, FiTrendingUp, FiArrowUp, FiXCircle, FiActivity, FiPercent, FiTarget } from "react-icons/fi"
import { useLanguage } from "../contexts/LanguageContext"

const StatsOverviewComponent = ({ stats, darkMode, secondaryTextColor, cardBg, cardBorder, containerVariants, itemVariants }) => {
  const { t } = useLanguage()

  const confidenceLabel = (v) =>
    v >= 80 ? t("confidence_excellent") : v >= 70 ? t("confidence_good") : t("confidence_needs_improvement")

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mb-8">
      {/* Header */}
      <div className={`flex items-center justify-between mb-4 md:mb-6`}>
        <div className={`flex items-center gap-2`}>
          <FiTarget className={`text-lg ${darkMode ? "text-blue-400" : "text-blue-600"}`} />
          <h2 className={`text-lg md:text-xl font-semibold ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
            {t("performance_overview")}
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
        {/* Total Products */}
        <motion.div variants={itemVariants} className={`${cardBg} ${cardBorder} backdrop-blur-sm rounded-xl p-4 border lg:col-span-2 shadow-sm hover:shadow-md transition-shadow duration-300`}>
          <div className={`flex items-start justify-between`}>
            <div className="space-y-2">
              <div className={`flex items-center gap-2`}>
                <div className={`p-2 rounded-lg ${darkMode ? "bg-blue-900/20" : "bg-blue-100"}`}>
                  <FiBarChart2 className={darkMode ? "text-blue-400" : "text-blue-600"} />
                </div>
                <p className={`${secondaryTextColor} text-sm font-medium`}>{t("total_products")}</p>
              </div>
              <div className="space-y-1">
                <p className="text-2xl md:text-3xl font-bold">{stats?.total || 0}</p>
                <div className={`flex items-center gap-1 text-xs`}>
                  <FiTrendingUp className="text-green-500" />
                  <span className={darkMode ? "text-green-400" : "text-green-600"}>{t("growth_last_month")}</span>
                </div>
              </div>
            </div>
            <div className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-700"}`}>
              {t("all_time")}
            </div>
          </div>
        </motion.div>

        {/* Stable */}
        <motion.div variants={itemVariants} className={`${cardBg} ${cardBorder} backdrop-blur-sm rounded-xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-300`}>
          <div className="space-y-3">
            <div className={`flex items-center justify-between`}>
              <div className={`flex items-center gap-2`}>
                <div className={`p-1.5 rounded-lg ${darkMode ? "bg-green-900/20" : "bg-green-100"}`}>
                  <FiCheckCircle className={darkMode ? "text-green-400" : "text-green-600"} />
                </div>
                <p className={`${secondaryTextColor} text-sm font-medium`}>{t("status_stable")}</p>
              </div>
              <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"}`}>
                {(stats?.stable / stats?.total * 100 || 0).toFixed(1)}%
              </div>
            </div>
            <div className="space-y-1">
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? "text-green-400" : "text-green-600"}`}>{stats?.stable || 0}</p>
              <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${(stats?.stable / stats?.total * 100) || 0}%` }} transition={{ duration: 1, delay: 0.2 }} className="h-full bg-green-500" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Opportunity */}
        <motion.div variants={itemVariants} className={`${cardBg} ${cardBorder} backdrop-blur-sm rounded-xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-300`}>
          <div className="space-y-3">
            <div className={`flex items-center justify-between`}>
              <div className={`flex items-center gap-2`}>
                <div className={`p-1.5 rounded-lg ${darkMode ? "bg-blue-900/20" : "bg-blue-100"}`}>
                  <FiTrendingUp className={darkMode ? "text-blue-400" : "text-blue-600"} />
                </div>
                <p className={`${secondaryTextColor} text-sm font-medium`}>{t("status_opportunity")}</p>
              </div>
              <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-100 text-blue-700"}`}>
                {(stats?.opportunity / stats?.total * 100 || 0).toFixed(1)}%
              </div>
            </div>
            <div className="space-y-1">
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>{stats?.opportunity || 0}</p>
              <div className={`flex items-center gap-1 text-xs`}>
                <FiArrowUp className="text-blue-500" />
                <span className={darkMode ? "text-blue-400" : "text-blue-600"}>{t("high_growth_potential")}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Critical */}
        <motion.div variants={itemVariants} className={`${cardBg} ${cardBorder} backdrop-blur-sm rounded-xl p-4 border shadow-sm hover:shadow-md transition-shadow duration-300`}>
          <div className="space-y-3">
            <div className={`flex items-center justify-between`}>
              <div className={`flex items-center gap-2`}>
                <div className={`p-1.5 rounded-lg ${darkMode ? "bg-red-900/20" : "bg-red-100"}`}>
                  <FiXCircle className={darkMode ? "text-red-400" : "text-red-600"} />
                </div>
                <p className={`${secondaryTextColor} text-sm font-medium`}>{t("status_critical")}</p>
              </div>
              <div className={`text-xs px-2 py-1 rounded-full ${darkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"}`}>
                {(stats?.critical / stats?.total * 100 || 0).toFixed(1)}%
              </div>
            </div>
            <div className="space-y-1">
              <p className={`text-xl md:text-2xl font-bold ${darkMode ? "text-red-400" : "text-red-600"}`}>{stats?.critical || 0}</p>
              <div className={`flex items-center gap-1 text-xs`}>
                <FiActivity className="text-red-500 animate-pulse" />
                <span className={darkMode ? "text-red-400" : "text-red-600"}>{t("requires_attention")}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Avg Confidence */}
        <motion.div variants={itemVariants} className={`${cardBg} ${cardBorder} backdrop-blur-sm rounded-xl p-4 border lg:col-span-2 shadow-sm hover:shadow-md transition-shadow duration-300`}>
          <div className="space-y-4">
            <div className={`flex items-center justify-between`}>
              <div className={`flex items-center gap-2`}>
                <div className={`p-1.5 rounded-lg ${darkMode ? "bg-purple-900/20" : "bg-purple-100"}`}>
                  <FiPercent className={darkMode ? "text-purple-400" : "text-purple-600"} />
                </div>
                <div className={""}>
                  <p className={`${secondaryTextColor} text-sm font-medium`}>{t("avg_confidence")}</p>
                  <p className="text-xs opacity-75">{t("ai_prediction_accuracy")}</p>
                </div>
              </div>
              <div className={`text-sm font-semibold ${stats?.averageConfidence >= 80 ? "text-green-500" : stats?.averageConfidence >= 70 ? "text-yellow-500" : "text-red-500"}`}>
                {stats?.averageConfidence || 0}%
              </div>
            </div>
            <div className="space-y-2">
              <div className="space-y-1">
                <div className={`flex justify-between text-xs`}>
                  <span className={secondaryTextColor}>{t("confidence_low")}</span>
                  <span className={secondaryTextColor}>{t("confidence_high")}</span>
                </div>
                <div className={`h-2 w-full ${darkMode ? "bg-gray-700" : "bg-gray-200"} rounded-full overflow-hidden`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${stats?.averageConfidence || 0}%` }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className={`h-full ${stats?.averageConfidence >= 80 ? "bg-gradient-to-r from-green-500 to-green-400" : stats?.averageConfidence >= 70 ? "bg-gradient-to-r from-yellow-500 to-yellow-400" : "bg-gradient-to-r from-red-500 to-red-400"}`}
                  />
                </div>
              </div>
              <div className={`flex items-center justify-between text-xs`}>
                <div className={`flex items-center gap-1`}>
                  <div className={`w-2 h-2 rounded-full ${stats?.averageConfidence >= 80 ? "bg-green-500" : stats?.averageConfidence >= 70 ? "bg-yellow-500" : "bg-red-500"}`} />
                  <span className={secondaryTextColor}>{confidenceLabel(stats?.averageConfidence || 0)}</span>
                </div>
                <div className={`flex items-center gap-1 ${stats?.averageConfidence >= 80 ? "text-green-500" : stats?.averageConfidence >= 70 ? "text-yellow-500" : "text-red-500"}`}>
                  <FiTrendingUp className="text-xs" />
                  <span>{t("confidence_growth")}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Mobile summary */}
        <div className="col-span-full sm:hidden">
          <div className={`${darkMode ? "bg-gray-800/50" : "bg-gray-50"} rounded-xl p-4 border`}>
            <div className={`flex items-center justify-between`}>
              <div className={""}>
                <p className={`${secondaryTextColor} text-sm font-medium`}>{t("performance_summary")}</p>
                <p className="text-xs opacity-75">{t("based_on_all_metrics")}</p>
              </div>
              <div className={`text-sm px-3 py-1 rounded-full ${darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"}`}>
                {stats?.averageConfidence >= 80 ? t("perf_strong") : stats?.averageConfidence >= 70 ? t("perf_moderate") : t("perf_weak")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default StatsOverviewComponent
